import { ApiError } from './errors/ApiError';
import { TeoEventTarget } from './utility/TeoEventTarget';

export interface TeoNotificationButton {
  label: string;
  variant?: 'primary' | 'secondary' | 'cta';
  disabled?: boolean;
  /**
   * The action to take when the button is clicked.
   *
   * Can be either:
   *   - a function to call that can also return the following values
   *   - a string according to #teo cmds
   *   - 'keep' to keep the alert open
   *   - void / undefined to close the alert
   */
  onclick?: `#teo/${string}` | 'keep' | (() => void | 'keep' | `#teo/${string}`);
}

export interface TeoNotificationData {
  id: number;
  title?: string;
  message?: string;

  mode?: 'info' | 'warning' | 'error';

  /**
   * The buttons to show in the alert.
   *
   * If not provided, the alert will only have a close button.
   */
  buttons?: TeoNotificationButton[];
}

export type TeoNotificationParams = Omit<TeoNotificationData, 'id'>;
type TeoNotificationEvents = {
  notificationsChanged: (alert: TeoNotificationData[]) => void;
};

export class TeoNotifications extends TeoEventTarget<TeoNotificationEvents> {
  private static nextId = 1;

  #notifications: TeoNotificationData[] = [];

  get currentNotification() {
    return this.#notifications.at(-1) ?? null;
  }

  addError(error: unknown) {
    // TODO: intelligent message generation
    return this.add({
      title: 'Systemfehler',
      message: error instanceof ApiError || error instanceof Error ? error.message : String(error),
      mode: 'error',
    });
  }

  add(notification: TeoNotificationParams) {
    const id = TeoNotifications.nextId++;
    this.#notifications = [
      ...this.#notifications,
      {
        id,
        ...notification,
        buttons: notification.buttons ?? [{ label: 'OK', variant: 'primary' }],
      },
    ];
    this.triggerNotificationsChanged();
    return id;
  }

  replaceAll(notification: TeoNotificationParams | null) {
    if (!notification && this.#notifications.length === 0) return;

    this.#notifications =
      notification ?
        [
          {
            id: TeoNotifications.nextId++,
            ...notification,
          },
        ]
      : [];
    this.triggerNotificationsChanged();
  }

  closeAll() {
    if (this.#notifications.length === 0) return;

    this.#notifications = [];
    this.triggerNotificationsChanged();
  }

  close(id: number) {
    this.#notifications = this.#notifications.filter((alert) => alert.id !== id);
    this.triggerNotificationsChanged();
  }

  private triggerNotificationsChanged() {
    this.triggerEvent('notificationsChanged', this.#notifications);
  }
}
